import {motion, stagger, useAnimate} from "framer-motion";
import React, {useEffect, useState} from "react";
import {cn, randomRange, spring} from "../lib/utils";
import {FloatingActionBar} from "./FloatingActionBar";
import {PatternLeft} from "./PatternLeft";
import {PatternRight} from "./PatternRight";
import {useCTAContent} from "./useCTAContent";
import {useTranslation} from "react-i18next";

interface CallToActionBlockProps {
  onAction: () => void;
  productJson: RootObject;
  selectedVariant: Variant;
}

const CallToActionBlock = ({
                             onAction,
                             productJson,
                             selectedVariant,
                           }: CallToActionBlockProps): React.ReactElement => {
  const {t} = useTranslation();
  const leftAnimation = useAnimate();
  const rightAnimation = useAnimate();
  const [inView, setInView] = useState(false);
  const cta = useCTAContent({productJson, selectedVariant});

  useEffect(() => {
    [leftAnimation, rightAnimation].forEach(([_, animate]) => {
      animate("path", {opacity: 0, scale: 0.5, x: 30}, {duration: 0});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function revealAnimation() {
    setInView(true);
    [leftAnimation, rightAnimation].forEach(([_, animate]) => {
      animate(
        "path",
        {opacity: 1, scale: 1, x: 0},
        {
          type: "spring",
          delay: stagger(0.025, {from: "last", startDelay: 0.15}),
        },
      );
      animate(
        "path",
        {
          rotate: [-3, 3],
          y: [randomRange(-5, 5), randomRange(5, 10)],
        },
        {
          repeat: Infinity,
          repeatType: "mirror",
          duration: 1.5,
          ease: "easeInOut",
          delay: stagger(-0.5),
        },
      );
    });
  }

  function revealWithDelay(delay: number) {
    return {
      animate: inView ? "inView" : "initial",
      style: {opacity: 0, scale: 0.5, y: 30},
      variants: {inView: {scale: 1, opacity: 1, y: 0}},
      transition: {...spring.smooth, damping: 60, delay: delay},
    };
  }

  return (
    <>
      <div className="relative flex flex-col items-center justify-center gap-2 py-6 pt-24 px-4 max-sm:pb-24">
        <motion.h1
          className="font-bold xl:text-6xl"
          viewport={{once: true, margin: "-20%"}}
          onViewportEnter={revealAnimation}
          {...revealWithDelay(0)}
        >
          {t("recipe_details.cta.title")}
        </motion.h1>
        <motion.p
          {...revealWithDelay(0.05)}
          className="text-green-900 text-lg tracking-wide text-center max-sm:text-sm"
        >
          {cta.supportParagraph}
        </motion.p>
        <PatternLeft
          className={cn(
            "max-w-[15%] h-[70vh] max-h-[48rem] absolute left-0 pointer-events-none",
            "md:max-w-[25%] max-md:max-h-[24rem] md:-z-10",
          )}
          preserveAspectRatio="xMaxYMid slice"
          ref={leftAnimation[0]}
        />
        <PatternRight
          className={cn(
            "max-w-[15%] h-[70vh] max-h-[48rem] absolute right-0 pointer-events-none",
            "md:max-w-[25%] max-md:max-h-[24rem] md:-z-10",
          )}
          preserveAspectRatio="xMinYMid slice"
          ref={rightAnimation[0]}
        />
      </div>
      <FloatingActionBar onAction={onAction} cta={cta} />
      <motion.div
        {...revealWithDelay(0.075)}
        className="flex flex-col items-center justify-center gap-2 py-6 pb-32 max-sm:hidden"
      >
        {cta.footNote && (
          <small className="tracking-wide">{cta.footNote}</small>
        )}
      </motion.div>
    </>
  );
};

export {CallToActionBlock};
